import {
  searchReportingFetch as fetch,
  searchReportingLegacyUrl,
} from '../../lib/flags/search-reporting';
// @ts-expect-error - The existing JavaScript record helpers have no declaration file.
import { donorCoversFees, getPaymentEditorSummary } from '../../utils/paymentRecord';
import { useState, useEffect, useMemo, useRef } from 'react';
import { getAuthToken, fetchWithAuthRetry } from '../../utils/auth';
import { editDonationHeaders } from '../../utils/editDonationRequest';
import { applyChargeTimeToDate } from '../../utils/chargeTimeUtils';
import ReactTooltip from 'react-tooltip';
import uuid from 'react-uuid';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { parsePaymentError } from '@charitystack/shared';
import FundraiserLayout from '../payment-types/FundraiserLayout';
import DeletePaymentModal from '../modals/DeletePaymentModal';
import { calculateDashboardPaymentFees } from '../../utils/dashboardFeeCalculation';
import { formatCurrency } from '../../utils/formatters';
import { useLatestCallback } from '../../hooks/useLatestCallback';
import { getRecordStatusInfo } from '@/components/workspaces/WorkspaceStatusSection';
import {
  EntityIdentityHeader,
  HeaderCloseAction,
  HEADER_ACTION_BUTTON_CLASS,
  WorkspaceSectionCard,
} from '../../../components/workspaces/EntityWorkspacePrimitives';
import { BanknotesIcon, ReceiptPercentIcon } from '@heroicons/react/24/outline';
import TaxDeductibleCard from '../payment-types/form-sections/TaxDeductibleCard';
// @ts-expect-error - JS file without type declarations
import { fieldMapper } from '../../utils/FieldMapper.js';
import type {
  CreateFundraisingPaymentDrawerProps,
  FundraisingFormData,
  FinixForm,
  StripeCardRef,
  FinixCardRef,
  FinixBankRef,
  Fund,
  CustomInput,
  ProductInfo,
  BackendFund,
  FormStep,
  FundraiserData,
  Contact,
  WorkspaceHeaderAction,
} from './types/FundraisingPaymentTypes';
import type { Merchant } from '../../types/common';
import { getEditPaymentType } from '../../utils/offlinePaymentMethods';

const MINIMUM_AMOUNT = 1.0;
const DEFAULT_PAYMENT_TYPE = 'Online';
const FORM_STEPS: FormStep[] = [
  'form_information',
  'custom_input_information',
  'contact_information',
  'dedication_information',
  'payment_information',
];

const getUnprefixedValidationField = (fieldName: string): string => {
  const stepPrefix = FORM_STEPS.find((step) => fieldName.startsWith(`${step}-`));
  return stepPrefix ? fieldName.slice(stepPrefix.length + 1) : fieldName;
};

const getValidationFieldAliases = (fieldName: string): string[] => {
  const aliases = new Set<string>([fieldName]);
  const unprefixedField = getUnprefixedValidationField(fieldName);
  aliases.add(unprefixedField);

  FORM_STEPS.forEach((step) => {
    if (!fieldName.startsWith(`${step}-`)) {
      aliases.add(`${step}-${unprefixedField}`);
    }
  });

  return Array.from(aliases);
};

const validationFieldBelongsToStep = (fieldName: string, step: FormStep): boolean => {
  if (fieldName.startsWith(`${step}-`)) {
    return true;
  }

  const unprefixedField = getUnprefixedValidationField(fieldName);
  switch (step) {
    case 'form_information':
      return (
        unprefixedField === 'donationAmount' ||
        unprefixedField === 'funds-empty' ||
        unprefixedField.startsWith('fund-') ||
        unprefixedField.startsWith('custom-input-')
      );
    case 'custom_input_information':
      return unprefixedField.startsWith('global-custom-');
    case 'contact_information':
      return ['contactSearch', 'firstName', 'lastName', 'email'].includes(unprefixedField);
    case 'dedication_information':
      return unprefixedField === 'dedicationName';
    case 'payment_information':
      return unprefixedField === 'offline_custom_fee';
    default:
      return false;
  }
};

const getCombinedValidationFields = (fields: Set<string>): Set<string> => {
  const combinedFields = new Set<string>();
  fields.forEach((field) => {
    combinedFields.add(field);
    combinedFields.add(getUnprefixedValidationField(field));
  });
  return combinedFields;
};

const clearStepValidationFields = (fields: Set<string>, step: FormStep): Set<string> => {
  const updatedFields = new Set<string>();
  fields.forEach((field) => {
    if (!validationFieldBelongsToStep(field, step)) {
      updatedFields.add(field);
    }
  });
  return updatedFields;
};

const formatSummaryCurrency = (amount: number): string => {
  return formatCurrency(Number.isFinite(amount) ? amount : 0);
};

// Helper function to get timezone - uses merchant timezone if available, otherwise browser timezone
const getTimezone = (merchant: Merchant | null): string => {
  return merchant?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Helper functions extracted to reduce redundancy
const isToday = (date: Date | string | null): boolean => {
  if (!date) return false;
  const today = new Date();
  const checkDate = new Date(date);
  return (
    today.getFullYear() === checkDate.getFullYear() &&
    today.getMonth() === checkDate.getMonth() &&
    today.getDate() === checkDate.getDate()
  );
};

const initialFormData: FundraisingFormData = {
  // Payment fields
  amount: 0,
  coverTransactionFee: true,
  coverFees: true,
  feeCovered: false,
  paymentMethod: 'Card',
  paymentType: DEFAULT_PAYMENT_TYPE,
  accountNumber: '',
  routingNumber: '',
  accountType: 'CHECKING',
  anonymous: false,
  cardToken: null,
  // Contact fields
  contactID: '',
  contactSearch: '',
  firstName: '',
  lastName: '',
  organizationName: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  region: '',
  zip: '',
  country: '',
  // Fundraising uses funds, not tickets
  funds: [
    {
      fundName: '',
      amount: 0,
      fundraiser: '',
      formID: '',
      productGroup: '',
      fundraiserHasSponsershipGroups: false,
      product: '',
      customInputs: [],
      quantity: 1,
      frequency: 'One-Time',
      nextChargeDate: null,
      endChargeDate: null,
      chargeTime: null,
    },
  ],
  formName: '',
  // Custom inputs
  globalCustomInputs: [],
};

export default function CreateFundraisingPaymentDrawer(props: CreateFundraisingPaymentDrawerProps) {
  // Derive payment processor locally — never mutate props.merchant.
  // Defaults to STRIPE (all new orgs are Stripe; FINIX is legacy).
  const effectivePaymentProcessor = props.merchant?.defaultPaymentProcessor || 'STRIPE';

  const isTaxExempt = props.merchant?.isTaxExempt !== false;
  const [donationAmount, setDonationAmount] = useState<string>('0');
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [feeCovered, setFeeCovered] = useState<boolean>(true);
  const [fees, setFees] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<FormStep>('form_information');
  const [totalWithoutFees, setTotalWithoutFees] = useState<number>(0);

  // Add edit mode detection
  const isEditMode = !!props.transaction;

  // Add merchant state
  const stripeCardRef = useRef<StripeCardRef | null>(null);
  const finixCardRef = useRef<FinixCardRef | null>(null);
  const finixBankRef = useRef<FinixBankRef | null>(null);
  const [form, setForm] = useState<FinixForm | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'Card' | 'Bank'>('Card');
  const [tokenID, setTokenID] = useState<string>('');
  const [formData, setFormData] = useState<FundraisingFormData>(initialFormData);
  // Add state for tracking invalid fields
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  // Add new state for tracking submission
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  // Contact selection state - moved from FundraiserLayout for validation access
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isNewContact, setIsNewContact] = useState<boolean>(false);

  const getEffectivePaymentType = (): string =>
    formData.paymentType || getEditPaymentType(props.transaction, DEFAULT_PAYMENT_TYPE);

  // Helper to check if payment is offline
  const isOfflinePayment = () => {
    if (!isEditMode || !props.transaction) return false;
    return getEffectivePaymentType() !== DEFAULT_PAYMENT_TYPE;
  };

  // Fetch contact data eagerly when drawer opens in edit mode
  // This ensures validation passes even if user doesn't visit Contact Information step
  useEffect(() => {
    if (
      !props.open ||
      !isEditMode ||
      !props.transaction?.contactID ||
      !props.merchant?.merchantName
    ) {
      return;
    }

    // Skip if we already have this contact loaded
    if (selectedContact?.id === props.transaction.contactID) {
      return;
    }

    const fetchContact = async () => {
      // Store transaction locally for type safety in async function
      const transaction = props.transaction;
      if (!transaction?.contactID) return;

      try {
        const contactResponse = await fetch(
          `${searchReportingLegacyUrl}/validate/contact?contactID=${transaction.contactID}`,
          {
            method: 'GET',
            headers: {
              accept: '*/*',
              'content-type': 'application/json',
              Authorization: await getAuthToken(),
            },
          },
        );

        if (!contactResponse.ok) {
          console.error('Contact network response was not ok');
          return;
        }

        const responseData = await contactResponse.json();
        const contactData = responseData.contactInformation;
        if (contactData) {
          const filteredContactData = {
            ...contactData,
            emails:
              contactData.emails?.filter((email: { deleted?: boolean }) => !email?.deleted) || [],
            addresses:
              contactData.addresses?.filter(
                (address: { deleted?: boolean }) => !address?.deleted,
              ) || [],
            phones:
              contactData.phones?.filter((phone: { deleted?: boolean }) => !phone?.deleted) || [],
          };

          setSelectedContact(filteredContactData || null);

          // Find matching email index
          let emailIdx = 0;
          if (
            transaction?.email &&
            transaction.email !== '-' &&
            filteredContactData.emails?.length > 0
          ) {
            const matchingEmailIndex = filteredContactData.emails.findIndex(
              (email: { value?: string }) => email.value === transaction.email,
            );
            emailIdx =
              matchingEmailIndex !== -1
                ? matchingEmailIndex
                : (filteredContactData.emails?.findIndex(
                    (email: { is_primary?: boolean }) => email.is_primary,
                  ) ?? 0);
          } else {
            emailIdx =
              filteredContactData.emails?.findIndex(
                (email: { is_primary?: boolean }) => email.is_primary,
              ) ?? 0;
          }
          const selectedEmail = filteredContactData.emails?.[emailIdx]?.value || '';

          // Find matching phone index
          let phoneIdx = 0;
          if (transaction?.phone && filteredContactData.phones?.length > 0) {
            const matchingPhoneIndex = filteredContactData.phones.findIndex(
              (phone: { value?: string }) => phone.value === transaction.phone,
            );
            phoneIdx =
              matchingPhoneIndex !== -1
                ? matchingPhoneIndex
                : (filteredContactData.phones?.findIndex(
                    (phone: { is_primary?: boolean }) => phone.is_primary,
                  ) ?? 0);
          } else {
            phoneIdx =
              filteredContactData.phones?.findIndex(
                (phone: { is_primary?: boolean }) => phone.is_primary,
              ) ?? 0;
          }
          const selectedPhone = filteredContactData.phones?.[phoneIdx]?.value || '';

          // Find matching address index
          let addressIdx = 0;
          if (
            (transaction?.address ||
              transaction?.address2 ||
              transaction?.city ||
              transaction?.region ||
              transaction?.zip ||
              transaction?.country) &&
            filteredContactData.addresses?.length > 0
          ) {
            const transactionAddress = [
              transaction.address,
              transaction.address2,
              transaction.city,
              transaction.region,
              transaction.zip,
              transaction.country,
            ]
              .filter(Boolean)
              .join(', ')
              .toLowerCase()
              .trim();

            const matchingAddressIndex = filteredContactData.addresses.findIndex(
              (addr: {
                line1?: string;
                line2?: string;
                city?: string;
                region?: string;
                postal?: string;
                country?: string;
              }) => {
                const contactAddress = [
                  addr.line1,
                  addr.line2,
                  addr.city,
                  addr.region,
                  addr.postal,
                  addr.country,
                ]
                  .filter(Boolean)
                  .join(', ')
                  .toLowerCase()
                  .trim();

                return contactAddress === transactionAddress;
              },
            );

            addressIdx =
              matchingAddressIndex !== -1
                ? matchingAddressIndex
                : (filteredContactData.addresses?.findIndex(
                    (addr: { is_primary?: boolean }) => addr.is_primary,
                  ) ?? 0);
          } else {
            addressIdx =
              filteredContactData.addresses?.findIndex(
                (addr: { is_primary?: boolean }) => addr.is_primary,
              ) ?? 0;
          }
          const selectedAddress =
            filteredContactData.addresses?.[addressIdx] ||
            filteredContactData.addresses?.find(
              (addr: { is_primary?: boolean }) => addr.is_primary,
            ) ||
            filteredContactData.addresses?.[0] ||
            {};

          // Pre-populate formData with contact information
          setFormData((prevData) => ({
            ...prevData,
            contactID: filteredContactData?.id || '',
            firstName: filteredContactData?.firstName || '',
            lastName: filteredContactData?.lastName || '',
            email: selectedEmail,
            phone: selectedPhone,
            address1: selectedAddress.line1,
            address2: selectedAddress.line2,
            city: selectedAddress.city,
            region: selectedAddress.region,
            zip: selectedAddress.postal,
            country: selectedAddress.country,
            organizationName: filteredContactData?.organizationName || '',
          }));
        }
      } catch (error) {
        console.error('Error fetching contact:', error);
      }
    };

    fetchContact();
  }, [
    props.open,
    isEditMode,
    props.transaction?.contactID,
    props.merchant?.merchantName,
    selectedContact?.id,
  ]);

  // Pre-populate form data when in edit mode OR reset when opening in create mode
  useEffect(() => {
    if (!props.open) return; // Only run when drawer opens

    // Reset form data when opening in create mode (not edit mode)
    if (!isEditMode) {
      setFormData({
        ...initialFormData,
        formToFormDataMap: formData.formToFormDataMap || {},
      });
      setDonationAmount('0');
      setFees(0);
      setFeeCovered(true);
      setIsDisabled(false);
      setInvalidFields(new Set());
      setPaymentMethod('Card');
      setCurrentStep('form_information');
      setSelectedContact(null);
      setIsNewContact(false);
      return;
    }

    if (isEditMode && props.transaction) {
      const transaction = props.transaction;

      // Helper function to convert frequency format
      const convertFrequency = (frequency: string | null | undefined): string => {
        if (!frequency) return 'One-Time';

        // Handle different frequency formats
        const frequencyMap: Record<string, string> = {
          ONE_TIME: 'One-Time',
          DAILY: 'Daily',
          WEEKLY: 'Weekly',
          MONTHLY: 'Monthly',
          ANNUALLY: 'Annually',
        };

        return frequencyMap[frequency] || frequency.replace(/_/g, '-');
      };

      // Convert funds data to match form structure
      const convertFunds = (funds: Fund[] | undefined): Fund[] => {
        if (!Array.isArray(funds)) return [];

        return funds.map((fund: Fund) => {
          // 🆔 Filter custom inputs by fundItemId to get only those belonging to this specific fund
          // This ensures each fund (even with same name/amount) shows its own custom inputs

          // Step 1: Get initial custom inputs (prioritize embedded array)
          let fundCustomInputs = [];

          if (fund.customInputs && fund.customInputs.length > 0) {
            // Start with embedded customInputs if available
            fundCustomInputs = fund.customInputs;
          } else {
            // Fallback: filter from top-level transaction.customInputs
            fundCustomInputs =
              transaction.customInputs?.filter(
                (input) =>
                  input.fundItemId === fund.fundItemId ||
                  (input.cartItemKey === fund.cartItemKey && input.inputType === 'fund'),
              ) || [];
          }

          // Step 2: CRITICAL - Filter by fundItemId to prevent showing wrong custom inputs
          // Even the embedded array might contain custom inputs from other funds with same formID
          if (fund.fundItemId && fundCustomInputs.length > 0) {
            fundCustomInputs = fundCustomInputs.filter(
              (input) =>
                input.fundItemId === fund.fundItemId || input.cartItemKey === fund.cartItemKey, // Fallback for backward compatibility
            );
          }

          return {
            fundName: fund.fundName || '',
            amount: fund.amount || 0,
            fundraiser: fund.fundraiser || '',
            formID: fund.formID || '',
            fundItemId: fund.fundItemId || null, // 🆔 Preserve fundItemId for tracking
            productGroup: fund.productGroup || '',
            fundraiserHasSponsershipGroups: !!(fund.productGroup && fund.productID),
            product: fund.productID
              ? {
                  productID: fund.productID,
                  productName: fund.productName || '',
                  productPrice: typeof fund.amount === 'number' ? fund.amount : 0,
                }
              : '',
            customInputs: fundCustomInputs,
            quantity: fund.quantity || 1,
            frequency: convertFrequency(fund.frequency) as Fund['frequency'],
            nextChargeDate: fund.nextChargeDate ? new Date(fund.nextChargeDate as string) : null,
            endChargeDate:
              fund.endChargeDate && fund.endChargeDate !== 'INFINITY'
                ? new Date(fund.endChargeDate as string)
                : null,
            chargeTime: fund.chargeTime || null,
          };
        });
      };

      // Convert custom inputs
      const convertCustomInputs = (customInputs: CustomInput[] | undefined): CustomInput[] => {
        if (!Array.isArray(customInputs)) return [];

        return customInputs.map((input: CustomInput) => ({
          ...input,
          id: input.id,
          value: input.answers?.[0] || '',
          title: input.title || input.label || '',
          options: input.options || [],
          type: input.type,
          required: input.required,
          inputType: input.inputType,
          global: input.global,
        }));
      };

      const newFormData: FundraisingFormData = {
        amount: parseFloat(String(transaction.grossAmount)) || 0,
        coverTransactionFee: donorCoversFees(transaction.coverFees),
        coverFees: donorCoversFees(transaction.coverFees),
        feeCovered: donorCoversFees(transaction.coverFees),
        // The operator's saved fee, so an untouched edit re-sends it as `customFee`
        // instead of zeroing it.
        customFeeAmount: donorCoversFees(transaction.coverFees)
          ? String(transaction.fees ?? '')
          : '',
        paymentMethod: transaction.paymentMethod === 'PAY_BY_BANK' ? 'Bank' : 'Card',
        accountNumber: transaction.accountNumber || '',
        routingNumber: transaction.routingNumber || '',
        accountType: transaction.bankAccountType || 'CHECKING',
        anonymous: transaction.anonymous || false,
        cardToken: null,
        email: transaction.email && transaction.email !== '-' ? transaction.email : '',
        firstName: transaction.name ? transaction.name.split(' ')[0] : '',
        lastName: transaction.name ? transaction.name.split(' ').slice(1).join(' ') : '',
        phone: transaction.phone || '',
        organizationName: transaction.organizationName || '',
        address1: transaction.address || '',
        address2: transaction.address2 || '',
        city: transaction.city || '',
        region: transaction.region || '',
        zip: transaction.zip || '',
        country: transaction.country || 'United States',
        paymentType: getEditPaymentType(transaction, DEFAULT_PAYMENT_TYPE),
        timestamp: transaction.timestamp || '',
        funds: convertFunds(transaction.funds),
        globalCustomInputs: convertCustomInputs(
          transaction.customInputs?.filter((input) => input.global) || [],
        ),
        purchaserCustomInputs: convertCustomInputs(
          transaction.customInputs?.filter((input) => input.inputType === 'purchaser') || [],
        ),
        formName: transaction.formName || '',
        // Dedication fields
        dedicationType: transaction.dedicationType || '',
        dedicationName: transaction.dedicationName || '',
        dedicationMessage: transaction.dedicationMessage || '',
        // Communication consent
        communicationConsent: transaction.communicationConsent || false,
      };

      setFormData(newFormData);
      setDonationAmount(String(transaction.grossAmount ?? '0'));
      setFeeCovered(donorCoversFees(transaction.coverFees));
      setFees(
        parseFloat(String(donorCoversFees(transaction.coverFees) ? transaction.fees : 0)) || 0,
      );
      setTotalWithoutFees(parseFloat(String(transaction.grossAmount)) || 0);
      setPaymentMethod(transaction.paymentMethod === 'PAY_BY_BANK' ? 'Bank' : 'Card');
    }
  }, [isEditMode, props.transaction, props.open]);

  // Add fund total calculation
  const fundsTotal = (funds: Fund[]): number => {
    if (!Array.isArray(funds)) return 0;

    return funds.reduce((total: number, fund: Fund) => {
      const amount = parseFloat(String(fund.amount)) || 0;
      const quantity = parseInt(String(fund.quantity)) || 1;
      const frequency = fund.frequency?.replace(/-/g, '_').toUpperCase();

      // Always include one-time funds
      if (frequency === 'ONE_TIME') {
        return total + amount * quantity;
      }

      if (!isEditMode && frequency !== 'ONE_TIME' && fund.chargeTime === 'now') {
        return total + amount * quantity;
      }

      // In edit mode, include all funds
      if (isEditMode) {
        return total + amount * quantity;
      }

      // Default: don't include
      return total;
    }, 0);
  };

  // Bind shared utility to merchant timezone so callers don't need to pass it every time
  const applyChargeTime = (date: Date | string | null, chargeTime: string | null) =>
    applyChargeTimeToDate(date, chargeTime, getTimezone(props.merchant));

  // Calculate fees based on the original amount.
  // This calculation must match checkout and backend plan-specific fee policy.
  const calculateFees = (
    amount: number | string,
    merchant: Merchant | null,
    paymentMethod: string,
  ): number => {
    return calculateDashboardPaymentFees({
      amount,
      merchant,
      paymentMethod,
      paymentProcessor: paymentMethod === 'Bank' ? 'FINIX' : undefined,
      coverFees: true,
      isEditOfExistingGift: isEditMode,
      subscription: props.linkedSubscription,
      // An edit keeps the gift's stored fee; nothing is re-priced (U1).
      existingGift: isEditMode ? props.transaction : undefined,
    });
  };

  // Calculate subscription fees for recurring funds only
  const calculateSubscriptionFees = (
    funds: Fund[],
    merchant: Merchant | null,
    paymentMethod: string,
  ): number => {
    if (!merchant || !Array.isArray(funds)) return 0;

    const subscriptionFunds = funds.filter(
      (fund: Fund) => fund.frequency && fund.frequency !== 'One-Time' && fund.chargeTime !== 'now',
    );

    if (subscriptionFunds.length === 0) return 0;

    const subscriptionAmount = subscriptionFunds.reduce((total: number, fund: Fund) => {
      const amount = parseFloat(String(fund.amount)) || 0;
      const quantity = parseInt(String(fund.quantity)) || 1;
      return total + amount * quantity;
    }, 0);

    // Always calculate fees as if they are covered (coverFees = true)
    return feeCovered ? calculateFees(subscriptionAmount, merchant, paymentMethod) : 0;
  };

  const fundsTotalWithoutFees = (funds: Fund[]): number => {
    if (!Array.isArray(funds)) return 0;

    return funds.reduce((total: number, fund: Fund) => {
      const amount = parseFloat(String(fund.amount)) || 0;
      const quantity = parseInt(String(fund.quantity)) || 1;
      const frequency = fund.frequency?.replace(/-/g, '_').toUpperCase();

      // Always include one-time funds
      if (frequency === 'ONE_TIME') {
        return total + amount * quantity;
      }

      if (!isEditMode && frequency !== 'ONE_TIME' && fund.chargeTime === 'now') {
        return total + amount * quantity;
      }

      // For recurring funds in creation mode, only include if nextChargeDate is today
      if (!isEditMode && frequency !== 'ONE_TIME') {
        // Don't include recurring funds in creation mode
        return total;
      }

      // In edit mode, include all funds
      if (isEditMode) {
        return total + amount * quantity;
      }

      // Default: don't include
      return total;
    }, 0);
  };

  // Update the main effect
  useEffect(() => {
    if (!props.merchant) {
      setFees(0);
      return;
    }

    // Calculate base amount from funds
    let baseAmount = fundsTotal(formData.funds);
    if (isNaN(baseAmount)) baseAmount = 0;
    setTotalWithoutFees(fundsTotalWithoutFees(formData.funds));

    // In edit mode, preserve the original fees from the transaction
    if (isEditMode && props.transaction) {
      const originalFees = parseFloat(
        String(donorCoversFees(props.transaction.coverFees) ? props.transaction.fees : 0),
      );
      setFees(originalFees);
    } else {
      // Calculate fees based on the original amount for new transactions
      const calculatedFees = calculateFees(baseAmount, props.merchant, paymentMethod);
      setFees(calculatedFees);
    }
  }, [formData.funds, feeCovered, paymentMethod, props.merchant, isEditMode, props.transaction]);

  // Update handleCustomAmountChange to maintain the original amount
  function handleCustomAmountChange(e: string | number): void {
    const value = parseFloat(String(e)) || 0;

    if (value >= 1) {
      setDonationAmount(value.toString());
      setFormData((prev) => ({
        ...prev,
        amount: value,
        // Don't set ticketTotal here - let the effect handle it
      }));
    } else {
      setDonationAmount('');
      setFormData((prev) => ({
        ...prev,
        amount: 0,
      }));
    }
  }

  // Handle fee coverage change
  const handleFeeCoverageChange = (covered: boolean, currentFees: number): void => {
    setFeeCovered(covered);
    setFormData((prev) => ({
      ...prev,
      coverTransactionFee: covered,
    }));
  };

  // Helper to validate a specific step and return invalid fields
  const validateStep = (step: FormStep): Set<string> => {
    const stepInvalidFields = new Set<string>();

    switch (step) {
      case 'form_information':
        {
          // Fundraiser validation
          // Basic amount validation
          const invalidStripeAmount =
            effectivePaymentProcessor === 'STRIPE' && parseFloat(donationAmount) >= 1000000;
          const invalidFinixAmount =
            effectivePaymentProcessor !== 'STRIPE' && parseFloat(donationAmount) >= 19000;
          if (
            !donationAmount ||
            parseFloat(donationAmount) < MINIMUM_AMOUNT ||
            invalidFinixAmount ||
            invalidStripeAmount
          ) {
            if (
              (invalidFinixAmount || invalidStripeAmount) &&
              getEffectivePaymentType() === DEFAULT_PAYMENT_TYPE
            ) {
              stepInvalidFields.add('form_information-donationAmount');
            }
          }
          // Fund validation - now checking each field individually
          if (!formData.funds || formData.funds.length === 0) {
            stepInvalidFields.add('form_information-funds-empty');
          } else {
            // --- Duplicate detection logic ---
            // Build a map to track unique cart keys and their indices
            const cartKeyMap = new Map();
            let hasDuplicate = false;
            const duplicateIndices = new Set<number>();

            formData.funds.forEach((fund, index) => {
              // Build the cart key based on sponsorship
              const keyParts = [
                fund.frequency || '',
                fund.fundName || '',
                fund.amount || '',
                fund.fundraiser || '',
              ];
              if (fund.fundraiserHasSponsershipGroups) {
                keyParts.push(fund.productGroup || '');
                keyParts.push(
                  typeof fund.product === 'object'
                    ? fund.product.productID
                    : String(fund.product || ''),
                );
              }

              // Include custom inputs in duplicate detection
              if (
                fund.customInputs &&
                Array.isArray(fund.customInputs) &&
                fund.customInputs.length > 0
              ) {
                // Sort by title to ensure consistent ordering, then map to "title:value" format
                const customInputsKey = fund.customInputs
                  .slice()
                  .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
                  .map((input) => `${input.title || ''}:${input.value || ''}`)
                  .join('||');
                keyParts.push(customInputsKey);
              } else {
                // Add empty marker if no custom inputs (so funds with/without custom inputs don't match)
                keyParts.push('');
              }

              const cartKey = keyParts.join('|');

              if (cartKeyMap.has(cartKey)) {
                // Duplicate found
                hasDuplicate = true;
                duplicateIndices.add(index);
              } else {
                cartKeyMap.set(cartKey, index);
              }
            });

            if (hasDuplicate) {
              // Mark all fields in the duplicate objects as invalid
              duplicateIndices.forEach((dupIdx) => {
                stepInvalidFields.add(`form_information-fund-duplicate-${dupIdx}`);
              });
            }

            // --- Standard validation ---
            formData.funds.forEach((fund, index) => {
              // If this fund is a duplicate, mark the whole object as error
              if (duplicateIndices.has(index)) {
                stepInvalidFields.add(`form_information-fund-duplicate-${index}`);
              }

              // Check amount
              if (
                (fund.fundraiserHasSponsershipGroups && fund.product && !fund.amount) ||
                parseFloat(String(fund.amount)) < MINIMUM_AMOUNT
              ) {
                stepInvalidFields.add(`form_information-fund-amount-${index}`);
              }
              if (
                (!fund.fundraiserHasSponsershipGroups && fund.fundName && !fund.amount) ||
                parseFloat(String(fund.amount)) < MINIMUM_AMOUNT
              ) {
                stepInvalidFields.add(`form_information-fund-amount-${index}`);
              }

              // Check fundraiser selection
              if (!fund.fundraiser || fund.fundraiser.trim() === '') {
                stepInvalidFields.add(`form_information-fund-fundraiser-${index}`);
              }

              // Check fund name selection
              if (!fund.fundName || fund.fundName.trim() === '') {
                stepInvalidFields.add(`form_information-fund-name-${index}`);
              }

              if (
                fund.fundraiserHasSponsershipGroups &&
                (!fund.productGroup || fund.productGroup.trim() === '')
              ) {
                stepInvalidFields.add(`form_information-fund-product-group-${index}`);
              }

              if (fund.fundraiserHasSponsershipGroups && !fund.product) {
                stepInvalidFields.add(`form_information-fund-product-${index}`);
              }

              // Check custom inputs if they exist and are required
              if (fund.customInputs) {
                fund.customInputs.forEach((input, inputIndex) => {
                  if (input.required && (!input.value || input.value.trim() === '')) {
                    stepInvalidFields.add(`form_information-custom-input-${index}-${inputIndex}`);
                  }
                });
              }
            });
          }
        }
        break;

      case 'dedication_information':
        // Validate dedication name is provided when a dedication type is selected
        if (
          formData.dedicationType &&
          (!formData.dedicationName || !formData.dedicationName.trim())
        ) {
          stepInvalidFields.add('dedication_information-dedicationName');
        }
        break;

      case 'custom_input_information':
        // Validate global (merchant) custom inputs
        if (formData.globalCustomInputs) {
          formData.globalCustomInputs.forEach((input, index) => {
            if (input.required && (!input.value || input.value.trim() === '')) {
              stepInvalidFields.add(`custom_input_information-global-custom-${index}`);
            }
          });
        }
        break;

      case 'contact_information': {
        // Validate contact selection - must have either an existing contact selected OR be creating a new one
        // In edit mode with a contactID, the contact exists but may not be fetched yet
        // (ContactInformation only mounts when its step is active)
        const hasContactSelected =
          selectedContact !== null ||
          isNewContact ||
          Boolean(formData.contactID) ||
          Boolean(formData.firstName?.trim() && formData.lastName?.trim()) ||
          (isEditMode && props.transaction?.contactID);
        if (!hasContactSelected) {
          stepInvalidFields.add('contact_information-contactSearch');
        }
        // Validate contact information fields (only if a contact context exists)
        if (hasContactSelected) {
          if (!formData.firstName?.trim()) {
            stepInvalidFields.add('contact_information-firstName');
          }
          if (!formData.lastName?.trim()) {
            stepInvalidFields.add('contact_information-lastName');
          }
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          stepInvalidFields.add('contact_information-email');
        }
        break;
      }

      case 'payment_information':
        if (
          getEffectivePaymentType() !== DEFAULT_PAYMENT_TYPE &&
          formData.feeCovered &&
          !formData.customFeeAmount
        ) {
          stepInvalidFields.add('payment_information-offline_custom_fee');
        }
        break;

      default:
        break;
    }

    return stepInvalidFields;
  };

  // Validate ALL steps and return combined invalid fields
  const validateAllSteps = (): Set<string> => {
    const allInvalidFields = new Set<string>();

    // Check if we should validate custom_input_information step
    const hasGlobalCustomInputs =
      (isEditMode && formData.globalCustomInputs && formData.globalCustomInputs.length > 0) ||
      (!isEditMode &&
        props.merchant?.globalCustomInputs &&
        props.merchant.globalCustomInputs.length > 0);

    // Check if any selected form has dedication enabled
    const hasDedicationEnabled = formData.funds?.some((fund) => {
      const fundraiserData = formData?.formToFormDataMap?.[fund.fundraiser];
      return (
        fundraiserData?.enableDedication &&
        fundraiserData?.dedicationTypes &&
        fundraiserData.dedicationTypes.length > 0
      );
    });

    // Define steps to validate based on mode
    // New order: form_information → custom_input_information → contact_information → dedication_information → payment_information
    const stepsToValidate: FormStep[] = ['form_information'];
    if (hasGlobalCustomInputs) {
      stepsToValidate.push('custom_input_information');
    }
    stepsToValidate.push('contact_information');
    if (hasDedicationEnabled) {
      stepsToValidate.push('dedication_information');
    }

    // Only validate payment step for non-online payments in create mode or offline edit mode
    if (!isEditMode || getEffectivePaymentType() !== DEFAULT_PAYMENT_TYPE) {
      stepsToValidate.push('payment_information');
    }

    // Validate each step
    stepsToValidate.forEach((step) => {
      const stepErrors = validateStep(step);
      stepErrors.forEach((error) => allInvalidFields.add(error));
    });

    return allInvalidFields;
  };

  const visibleInvalidFields = useMemo((): Set<string> => {
    if (!invalidFields || invalidFields.size === 0) {
      return invalidFields;
    }

    const currentValidationFields = getCombinedValidationFields(validateAllSteps());
    const visibleFields = new Set<string>();
    invalidFields.forEach((field) => {
      if (currentValidationFields.has(field)) {
        visibleFields.add(field);
      }
    });
    return visibleFields;
  }, [formData, invalidFields, selectedContact, isNewContact]);

  useEffect(() => {
    setInvalidFields((prev) => {
      if (!prev || prev.size === 0) {
        return prev;
      }

      const activeValidationFields = getCombinedValidationFields(validateAllSteps());
      const prunedFields = new Set<string>();
      prev.forEach((field) => {
        if (activeValidationFields.has(field)) {
          prunedFields.add(field);
        }
      });

      if (prunedFields.size === prev.size) {
        return prev;
      }

      return prunedFields;
    });
  }, [formData, selectedContact, isNewContact]);

  // Continue button - validates only the current step
  const handleContinue = () => {
    const stepErrors = validateStep(currentStep);
    const newInvalidFields = getCombinedValidationFields(stepErrors);

    // Check for duplicate funds and show toast
    const hasDuplicates = Array.from(stepErrors).some((field) => field.includes('fund-duplicate'));
    if (hasDuplicates) {
      toast.error('Consolidate conflicting fields between fundraisers');
    }

    if (newInvalidFields.size > 0) {
      setInvalidFields(newInvalidFields);

      // Add descriptive error message based on current step (only if not duplicate error)
      if (!hasDuplicates) {
        const stepErrorMessages = {
          form_information: 'Please complete all required fundraiser information',
          dedication_information: 'Please enter a name for the dedication',
          custom_input_information: 'Please complete all required custom fields',
          contact_information: 'Please complete all required contact information',
          payment_information: 'Please complete all required payment information',
        };

        toast.error(
          stepErrorMessages[currentStep as keyof typeof stepErrorMessages] ||
            'Please complete all required fields',
        );
      }
    } else {
      setInvalidFields((prev) => clearStepValidationFields(prev, currentStep));

      // Check if we should skip the custom_input_information step
      const hasGlobalCustomInputs =
        (isEditMode && formData.globalCustomInputs && formData.globalCustomInputs.length > 0) ||
        (!isEditMode &&
          props.merchant?.globalCustomInputs &&
          props.merchant.globalCustomInputs.length > 0);

      // Check if any selected form has dedication enabled
      const hasDedicationEnabled = formData.funds?.some((fund) => {
        const fundraiserData = formData?.formToFormDataMap?.[fund.fundraiser];
        return (
          fundraiserData?.enableDedication &&
          fundraiserData?.dedicationTypes &&
          fundraiserData.dedicationTypes.length > 0
        );
      });

      // Determine next step based on current step and available features
      // New order: form_information → custom_input_information → contact_information → dedication_information → payment_information
      let nextStep: FormStep | null = null;
      switch (currentStep) {
        case 'form_information':
          nextStep = hasGlobalCustomInputs ? 'custom_input_information' : 'contact_information';
          break;
        case 'custom_input_information':
          nextStep = 'contact_information';
          break;
        case 'contact_information':
          nextStep = hasDedicationEnabled ? 'dedication_information' : 'payment_information';
          break;
        case 'dedication_information':
          nextStep = 'payment_information';
          break;
      }

      if (nextStep) {
        setCurrentStep(nextStep);
      }
    }
  };

  // Handle Confirm Payment / Save Payment button - validates ALL steps
  const handleConfirmPayment = () => {
    const allErrors = validateAllSteps();

    if (allErrors.size > 0) {
      setInvalidFields(getCombinedValidationFields(allErrors));

      // Check for duplicate funds and show toast
      const hasDuplicates = Array.from(allErrors).some((field) => field.includes('fund-duplicate'));
      if (hasDuplicates) {
        toast.error('Consolidate conflicting fields between fundraisers');
      } else {
        toast.error('Please fix all validation errors before submitting');
      }
      return;
    }

    // All validations passed - clear any stale invalid fields and proceed with payment
    setInvalidFields(new Set());

    if (isEditMode) {
      savePayment();
    } else {
      sendToPaymentScreen();
    }
  };
  const handleBack = () => {
    // Check if we should skip the custom_input_information step
    const hasGlobalCustomInputs =
      (isEditMode && formData.globalCustomInputs && formData.globalCustomInputs.length > 0) ||
      (!isEditMode &&
        props.merchant?.globalCustomInputs &&
        props.merchant.globalCustomInputs.length > 0);

    // Check if any selected form has dedication enabled
    const hasDedicationEnabled = formData.funds?.some((fund) => {
      const fundraiserData = formData?.formToFormDataMap?.[fund.fundraiser];
      return (
        fundraiserData?.enableDedication &&
        fundraiserData?.dedicationTypes &&
        fundraiserData.dedicationTypes.length > 0
      );
    });

    // New order: form_information → custom_input_information → contact_information → dedication_information → payment_information
    switch (currentStep) {
      case 'custom_input_information':
        setCurrentStep('form_information');
        break;
      case 'contact_information':
        setCurrentStep(hasGlobalCustomInputs ? 'custom_input_information' : 'form_information');
        break;
      case 'dedication_information':
        setCurrentStep('contact_information');
        break;
      case 'payment_information':
        setCurrentStep(hasDedicationEnabled ? 'dedication_information' : 'contact_information');
        break;
    }
  };

  // Helper function to check if a specific field is invalid
  const isFieldInvalid = (fieldId: string): boolean => {
    return invalidFields.has(fieldId);
  };

  // Update sendToPaymentScreen to handle submission state
  const sendToPaymentScreen = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    let stripeResult: { success: boolean; paymentMethod?: unknown; error?: unknown } | undefined =
      undefined;
    let finixCardErrors: string[] | undefined = undefined;
    let finixBankErrors: string[] | undefined = undefined;
    let stripePaymentMethod: unknown = undefined;

    // Payment processor validation
    if (getEffectivePaymentType() !== DEFAULT_PAYMENT_TYPE) {
      createOfflineDonation();
    } else {
      if (effectivePaymentProcessor == 'STRIPE' && paymentMethod === 'Card') {
        if (stripeCardRef.current) {
          stripeResult = await stripeCardRef.current.handleSubmit();
          if (stripeResult.success) {
            stripePaymentMethod = stripeResult.paymentMethod;
          }
        }
      } else {
        if (paymentMethod === 'Card' && finixCardRef.current) {
          finixCardErrors = await finixCardRef.current.checkIframeErrors();
        }
        if (paymentMethod === 'Bank' && finixBankRef.current) {
          finixBankErrors = await finixBankRef.current.checkValidity();
        }
      }

      // Single validation check
      if (
        (stripeResult != undefined && !stripeResult.success) ||
        (finixCardErrors != undefined && finixCardErrors.length > 0) ||
        (finixBankErrors != undefined && finixBankErrors.length > 0)
      ) {
        // setInvalidFields(newInvalidFields);
        setIsSubmitting(false);
        return;
      }

      if (paymentMethod === 'Card') {
        let tokenID = '';

        // Grab finix token if not stripe
        if (effectivePaymentProcessor !== 'STRIPE' && form) {
          form.submit(
            import.meta.env.VITE_APP_FINIX_ENVIRONMENT,
            import.meta.env.VITE_APP_FINIX_APPLICATION_ID,
            async function (err, res) {
              if (err) {
                const parsedError = parsePaymentError(err, null, null, 'FINIX');
                toast.error(parsedError.userMessage);
                setIsSubmitting(false);
                return;
              }
              tokenID = res.data.id;
              createDonationBackend(tokenID, stripePaymentMethod, effectivePaymentProcessor);
            },
          );
        }
        // If stripe, create donation card
        else {
          createDonationBackend('', stripePaymentMethod, effectivePaymentProcessor);
        }
      } else if (paymentMethod === 'Bank') {
        createDonationBackend('', {}, effectivePaymentProcessor);
      }
    }
  };

  // Add handler for clearing individual field errors
  const handleFieldFocus = (fieldName: string): void => {
    // Remove both field-level and section-prefixed errors for this field.
    setInvalidFields((prev) => {
      const newInvalidFields = new Set(prev);
      getValidationFieldAliases(fieldName).forEach((alias) => newInvalidFields.delete(alias));

      return newInvalidFields;
    });
  };

  const createOfflineDonation = async () => {
    // Combine all custom inputs into a single array with type identifiers
    const customInputs = [
      // Global custom inputs
      ...(formData.globalCustomInputs?.map((input) => {
        const { value, ...inputWithoutValue } = input;
        return {
          ...inputWithoutValue,
          answers: [value],
          inputType: 'global',
          global: true,
        };
      }) || []),

      // Purchaser custom inputs
      ...(formData.purchaserCustomInputs?.map((input) => {
        const { value, ...inputWithoutValue } = input;
        return {
          ...inputWithoutValue,
          answers: [value],
          inputType: 'purchaser',
        };
      }) || []),

      // Fund custom inputs
      ...(formData.funds?.flatMap((fund) =>
        fund.customInputs?.map((input) => {
          const { value, ...inputWithoutValue } = input;
          return {
            ...inputWithoutValue,
            answers: [value],
            inputType: 'fund',
            fundId: fund.formID,
            cartItemKey: `${fund.frequency}_${fund.fundName}_${parseFloat(
              String(fund.amount),
            ).toFixed(2)}_${fund.fundraiser}`,
          };
        }),
      ) || []),
    ];

    const copyFunds: BackendFund[] = JSON.parse(JSON.stringify(formData.funds));

    // remove customInputs from funds array
    copyFunds.forEach((fund: BackendFund) => {
      fund.productID =
        fund.product && typeof fund.product === 'object'
          ? (fund.product as ProductInfo).productID
          : '';
      fund.productName =
        fund.product && typeof fund.product === 'object'
          ? (fund.product as ProductInfo).productName
          : '';
      delete fund.product;
      delete fund.fundraiserHasSponsershipGroups;
      delete fund.chargeTime;
      fund.amount = parseFloat(String(fund.amount));
      fund.frequency = String(fund.frequency).replace(/-/g, '_').toUpperCase();

      // Include custom input answers in cartItemKey to differentiate funds with same properties but different answers
      let customInputsSuffix = '';
      if (fund.customInputs && Array.isArray(fund.customInputs) && fund.customInputs.length > 0) {
        customInputsSuffix =
          '_' +
          fund.customInputs
            .slice()
            .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
            .map((input) => `${input.title || ''}:${input.value || ''}`)
            .join('_')
            .replace(/[^a-zA-Z0-9_]/g, '_'); // Sanitize special characters
      }

      fund.cartItemKey = `${String(fund.frequency).replace(/-/g, '_').toUpperCase()}_${
        fund.fundName
      }_${parseFloat(String(fund.amount)).toFixed(2)}_${fund.fundraiser}${customInputsSuffix}`;
      const mappedFields = fieldMapper(
        formData.formToFormDataMap?.[fund.fundraiser]?.fieldMappings ?? [],
        fund.fundName,
        // 🆔 Filter by fundItemId to match custom inputs to their specific fund
        // This ensures funds with different custom input answers are properly distinguished
        customInputs.filter((input) => {
          // Priority 1: If both have fundItemId, use strict matching
          if (fund.fundItemId && input.fundItemId) {
            return input.fundItemId === fund.fundItemId;
          }
          // Priority 2: Fallback to cartItemKey when fundItemId not available
          if (fund.cartItemKey && input.cartItemKey) {
            return input.cartItemKey === fund.cartItemKey;
          }
          // Priority 3: Legacy fallback - match by fundId
          return input.fundId === fund.formID;
        }) || [],
        [],
      );
      // add all key values of mappedFields to fund
      Object.assign(fund, mappedFields);
      delete fund.customInputs;

      // For offline payments, remove date fields since they're just records
      delete fund.nextChargeDate;
      delete fund.endChargeDate;
    });

    // remove customInputs from funds array
    const schedulePayment = copyFunds.reduce((acc: Record<string, unknown>, fund: BackendFund) => {
      // Create unique key by combining frequency, fund name, amount and fundraiser
      const key = fund.cartItemKey || '';

      acc[key] = {
        amount: parseFloat(String(fund.amount)),
        fund: fund.fundName,
        frequency: fund.frequency,
        fundraiser: fund.fundraiser,
        formID: fund.formID,
        quantity: fund.quantity,
        showComponent: false,
        aggregate: null,
        chargeTime: null,
      };

      if (fund.nextChargeDate && fund.frequency !== 'ONE_TIME') {
        const startValueWithTime = applyChargeTime(fund.nextChargeDate, null);
        (acc[key] as Record<string, unknown>).startValue = startValueWithTime;
      }
      if (
        fund.endChargeDate &&
        fund.endChargeDate !== 'INFINITY' &&
        fund.frequency !== 'ONE_TIME'
      ) {
        // Apply charge time to the end value
        const endValueWithTime = applyChargeTime(fund.endChargeDate, fund.chargeTime ?? null);
        (acc[key] as Record<string, unknown>).endValue = endValueWithTime;
      }

      return acc;
    }, {});

    // Calculate total amount including all funds for offline payments
    const offlineTotalAmount = copyFunds.reduce((total: number, fund: BackendFund) => {
      const amount = parseFloat(String(fund.amount)) || 0;
      const quantity = parseInt(String(fund.quantity)) || 1;
      return total + amount * quantity;
    }, 0);

    const paymentProcessor = effectivePaymentProcessor;
    // Prepare request body with consolidated customInputs
    const baseRequestBody = {
      token: tokenID,
      contactID: formData.contactID,
      merchantName: props.merchant?.merchantName || '',
      defaultPaymentProcessor: paymentProcessor,
      stripeAccountID: props.merchant?.stripeAccountID ?? '',
      stripePaymentMethod: '',
      paymentMethod: 'OFFLINE',
      paymentType: getEffectivePaymentType(),
      anonymous: formData.anonymous,
      amount:
        getEffectivePaymentType() !== DEFAULT_PAYMENT_TYPE ? offlineTotalAmount : totalWithoutFees,
      customFee: formData.feeCovered ? parseFloat(String(formData.customFeeAmount || 0)) : 0,
      tip: 0,
      ...(formData.email && formData.email.trim() && formData.email !== '-'
        ? { email: formData.email }
        : {}),
      uuid: String(uuid()),
      coverFees: formData.feeCovered,
      name: `${formData.firstName} ${formData.lastName}`,
      phone: formData.phone ?? '',
      address: formData.address1 ?? '',
      address2: formData.address2 ?? '',
      city: formData.city ?? '',
      region: formData.region ?? '',
      zip: formData.zip ?? '',
      country: formData.country ?? '',
      fund: copyFunds && copyFunds.length > 0 ? copyFunds[0].fundName : '',
      formName: copyFunds && copyFunds.length > 0 ? copyFunds[0].fundraiser : '',
      formID: copyFunds && copyFunds.length > 0 ? copyFunds[0].formID : '',
      customInputs: customInputs,
      orgName: formData.organizationName,
      funds: copyFunds && copyFunds.length > 0 ? copyFunds : [],
      schedulePayment: schedulePayment,
      elementID: '', // add later
      tickets: null, // add in later
      timezone: getTimezone(props.merchant),
      timestamp: formData.timestamp,
      taxDeductible: formData.taxDeductible ?? isTaxExempt,
      // Dedication fields
      dedicationType: formData.dedicationType || '',
      dedicationName: formData.dedicationName || '',
      dedicationMessage: formData.dedicationMessage || '',
      // Communication consent
      communicationConsent: formData.communicationConsent || false,
    };

    // const isFreeTickets =
    //   (formData.amount == 0 || formData.amount == discount) &&
    //   createPaymentType === "event";

    // if (isFreeTickets) {
    //   baseRequestBody.fund = "FREE";
    //   baseRequestBody.promoCode = selectedPromoCode;
    // }

    // Add type-specific fields
    const requestBody = {
      ...baseRequestBody,
      elementID: formData.formToFormDataMap?.[copyFunds[0].fundraiser]?.elementID ?? '',
      formID: formData.formToFormDataMap?.[copyFunds[0].fundraiser]?.elementID ?? '',
    };

    // Add processor-specific endpoint
    const endpoint = `${
      import.meta.env.VITE_APP_DASHBOARD_TRANSACTIONS_URL
    }/checkout/create-donation`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(requestBody),
      });

      const responseBody = await response.json();

      if (!response.ok) {
        const parsedError = parsePaymentError(
          null,
          response,
          responseBody,
          effectivePaymentProcessor || 'STRIPE',
        );
        console.error('Payment error:', parsedError.code, parsedError.userMessage, responseBody);
        toast.error(parsedError.userMessage);
        setIsSubmitting(false);
        return;
      }

      // Success handling
      props.grabSelectableFunds?.(await getAuthToken());
      props.setOpen(false);
      clearStates();
      setIsSubmitting(false);

      // Call the callback with the new transaction data if provided
      if (props.onTransactionCreated && responseBody) {
        // Check if responseBody is an array or single object
        if (Array.isArray(responseBody)) {
          // Handle array of donation objects - use the new method if available
          if (props.onMultipleTransactionsCreated) {
            props.onMultipleTransactionsCreated(responseBody);
          } else {
            // Fallback to individual calls
            responseBody.forEach((donation) => {
              props.onTransactionCreated?.(donation);
            });
          }
        } else {
          // Handle single donation object
          props.onTransactionCreated(responseBody);
        }
      }

      // Success message based on processor
      const successMessage = 'Payment submitted successfully!';
      toast.success(successMessage);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error(errorMessage);

      // Log detailed error for debugging
      console.error('Payment processing error:', {
        processor: paymentProcessor,
        error: error,
        requestBody: requestBody,
      });
      setIsSubmitting(false);
    }
  };

  const createDonationBackend = async (
    tokenID: string,
    stripePaymentMethod: unknown,
    paymentProcessor: string | undefined,
  ) => {
    try {
      // 🆔 Generate fundItemId for any funds that don't have one (new funds)
      // Do this BEFORE building customInputs array so the IDs are available
      formData.funds.forEach((fund) => {
        if (!fund.fundItemId) {
          fund.fundItemId = `fund_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }
      });

      // Combine all custom inputs into a single array with type identifiers
      const customInputs = [
        // Global custom inputs
        ...(formData.globalCustomInputs?.map((input) => {
          const { value, ...inputWithoutValue } = input;
          return {
            ...inputWithoutValue,
            answers: [value],
            inputType: 'global',
            global: true,
          };
        }) || []),

        // Purchaser custom inputs
        ...(formData.purchaserCustomInputs?.map((input) => {
          const { value, ...inputWithoutValue } = input;
          return {
            ...inputWithoutValue,
            answers: [value],
            inputType: 'purchaser',
          };
        }) || []),

        // Fund custom inputs
        ...(formData.funds?.flatMap((fund) => {
          // Calculate customInputsSuffix for this fund (same logic as later in copyFunds.forEach)
          let customInputsSuffix = '';
          if (
            fund.customInputs &&
            Array.isArray(fund.customInputs) &&
            fund.customInputs.length > 0
          ) {
            customInputsSuffix =
              '_' +
              fund.customInputs
                .slice()
                .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
                .map((input) => `${input.title || ''}:${input.value || ''}`)
                .join('_')
                .replace(/[^a-zA-Z0-9_]/g, '_'); // Sanitize special characters
          }

          return fund.customInputs?.map((input) => {
            const { value, ...inputWithoutValue } = input;
            const frequency = fund.frequency.replace(/-/g, '_').toUpperCase();
            return {
              ...inputWithoutValue,
              answers: [value],
              inputType: 'fund',
              fundId: fund.formID,
              fundItemId: fund.fundItemId, // 🆔 Link custom input to its parent fund
              cartItemKey: `${frequency}_${fund.fundName}_${parseFloat(String(fund.amount)).toFixed(
                2,
              )}_${fund.fundraiser}${customInputsSuffix}`, // Include suffix to match fund's cartItemKey
            };
          });
        }) || []),
      ];

      // Determine payment processor specific details
      const paymentDetails: Record<
        string,
        {
          paymentMethod: string;
          stripePaymentMethod?: unknown;
          finixToken?: string;
          processorType: string;
        }
      > = {
        STRIPE: {
          paymentMethod: 'CARD',
          stripePaymentMethod,
          processorType: 'STRIPE',
        },
        FINIX: {
          paymentMethod: 'CARD',
          finixToken: tokenID,
          processorType: 'FINIX',
        },
      };

      let processorDetails = paymentDetails[paymentProcessor || 'FINIX'];

      if (paymentMethod === 'Bank') {
        paymentDetails['FINIX'] = {
          ...paymentDetails['FINIX'],
          paymentMethod: 'PAY_BY_BANK',
        };
        processorDetails = paymentDetails['FINIX'];
      }

      // Get processor-specific details

      const copyFunds: BackendFund[] = JSON.parse(JSON.stringify(formData.funds));

      // Process funds and handle today's date logic
      const processedFunds: BackendFund[] = [];
      const finalAmount = fundsTotalWithoutFees(formData.funds);

      copyFunds.forEach((fund: BackendFund) => {
        // 🆔 Generate fundItemId if it doesn't exist (for new funds)
        // Keep existing fundItemId for funds being edited or legacy funds
        if (!fund.fundItemId) {
          fund.fundItemId = `fund_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }

        fund.productID =
          fund.product && typeof fund.product === 'object'
            ? (fund.product as ProductInfo).productID
            : '';
        fund.productName =
          fund.product && typeof fund.product === 'object'
            ? (fund.product as ProductInfo).productName
            : '';
        delete fund.product;
        delete fund.fundraiserHasSponsershipGroups;
        const frequency = fund.frequency.replace(/-/g, '_').toUpperCase();

        // Apply charge time to nextChargeDate
        // If chargeTime is "now", don't set nextChargeDate - backend will handle it
        // If nextChargeDate is not set for a subscription, default to today (unless chargeTime is "now")
        if (frequency !== 'ONE_TIME') {
          if (fund.chargeTime === 'now') {
            // When chargeTime is "now", set nextChargeDate to null - backend handles immediate processing
            fund.nextChargeDate = null;
          } else if (fund.nextChargeDate) {
            // Apply the specific charge time to the selected date
            const nextChargeDateWithTime = applyChargeTime(
              fund.nextChargeDate,
              fund.chargeTime ?? null,
            );
            fund.nextChargeDate = nextChargeDateWithTime;
          } else {
            // No nextChargeDate selected and not "now" - default to today with the charge time
            const dateToUse = new Date();
            const nextChargeDateWithTime = applyChargeTime(dateToUse, fund.chargeTime ?? null);
            fund.nextChargeDate = nextChargeDateWithTime;
          }
        }

        if (fund.endChargeDate && fund.endChargeDate !== 'INFINITY') {
          // Use the same chargeTime for endChargeDate - applyChargeTime will convert it properly
          // For "now", we still need to convert endChargeDate properly, so get current time in merchant timezone
          const endChargeDateWithTime = applyChargeTime(
            fund.endChargeDate,
            fund.chargeTime ?? null,
          );
          fund.endChargeDate = endChargeDateWithTime;
        } else {
          fund.endChargeDate = 'INFINITY';
        }

        fund.amount = parseFloat(String(fund.amount));
        fund.frequency = frequency;

        // Include custom input answers in cartItemKey to differentiate funds with same properties but different answers
        let customInputsSuffix = '';
        if (fund.customInputs && Array.isArray(fund.customInputs) && fund.customInputs.length > 0) {
          customInputsSuffix =
            '_' +
            fund.customInputs
              .slice()
              .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
              .map((input) => `${input.title || ''}:${input.value || ''}`)
              .join('_')
              .replace(/[^a-zA-Z0-9_]/g, '_'); // Sanitize special characters
        }

        fund.cartItemKey = `${frequency}_${fund.fundName}_${parseFloat(String(fund.amount)).toFixed(
          2,
        )}_${fund.fundraiser}${customInputsSuffix}`;

        const mappedFields = fieldMapper(
          formData.formToFormDataMap?.[fund.fundraiser]?.fieldMappings ?? [],
          fund.fundName,
          // 🆔 Filter by fundItemId to match custom inputs to their specific fund
          // This ensures funds with different custom input answers are properly distinguished
          customInputs.filter((input) => {
            // Priority 1: If both have fundItemId, use strict matching
            if (fund.fundItemId && input.fundItemId) {
              return input.fundItemId === fund.fundItemId;
            }
            // Priority 2: Fallback to cartItemKey when fundItemId not available
            if (fund.cartItemKey && input.cartItemKey) {
              return input.cartItemKey === fund.cartItemKey;
            }
            // Priority 3: Legacy fallback - match by fundId
            return input.fundId === fund.formID;
          }) || [],
          [],
        );
        // add all key values of mappedFields to fund
        Object.assign(fund, mappedFields);
        delete fund.customInputs;

        // Add the processed fund
        processedFunds.push(fund);
      });

      // Update copyFunds with processed funds
      copyFunds.length = 0;
      copyFunds.push(...processedFunds);

      // remove customInputs from funds array
      const schedulePayment = copyFunds.reduce(
        (acc: Record<string, Record<string, unknown>>, fund: BackendFund) => {
          // Create unique key by combining frequency, fund name, amount and fundraiser
          const key = fund.cartItemKey || '';

          acc[key] = {
            amount: parseFloat(String(fund.amount)),
            fund: fund.fundName,
            frequency: fund.frequency,
            fundraiser: fund.fundraiser,
            formID: fund.formID || '',
            quantity: fund.quantity,
            showComponent: false,
            aggregate: null,
            chargeTime: fund.chargeTime || null,
          };

          if (fund.nextChargeDate && fund.frequency !== 'ONE_TIME') {
            // Apply charge time to the start value
            const startValueWithTime = applyChargeTime(
              fund.nextChargeDate,
              fund.chargeTime ?? null,
            );
            acc[key].startValue = startValueWithTime;
          }
          if (
            fund.endChargeDate &&
            fund.endChargeDate !== 'INFINITY' &&
            fund.frequency !== 'ONE_TIME'
          ) {
            // Extract the time from the processed nextChargeDate to apply the same time to endChargeDate
            let timeToApply: string | null = fund.chargeTime ?? null;
            if (fund.nextChargeDate && fund.frequency !== 'ONE_TIME') {
              // If nextChargeDate was processed, extract the time from it
              const nextDate = new Date(fund.nextChargeDate);
              const hours = nextDate.getHours().toString().padStart(2, '0');
              const minutes = nextDate.getMinutes().toString().padStart(2, '0');
              timeToApply = `${hours}:${minutes}`;
            }

            const endValueWithTime = applyChargeTime(fund.endChargeDate, timeToApply);
            acc[key].endValue = endValueWithTime;
          }

          return acc;
        },
        {},
      );

      // Prepare request body with consolidated customInputs
      const baseRequestBody = {
        ...processorDetails,
        token: tokenID,
        contactID: formData.contactID,
        accountNumber: formData.accountNumber ?? '',
        routingNumber: formData.routingNumber ?? '',
        accountType: formData.accountType ?? '',
        merchantName: props.merchant?.merchantName || '',
        defaultPaymentProcessor: processorDetails.processorType,
        stripeAccountID: props.merchant?.stripeAccountID ?? '',
        stripePaymentMethod: stripePaymentMethod ?? '',
        paymentMethod: processorDetails.paymentMethod,
        anonymous: formData.anonymous,
        customInputs: customInputs,
        amount: finalAmount,
        fees: fees || 0,
        coverFees: feeCovered,
        ...(formData.email && formData.email.trim() && formData.email !== '-'
          ? { email: formData.email }
          : {}),
        uuid: String(uuid()),
        name: `${formData.firstName} ${formData.lastName}`,
        phone: formData.phone ?? '',
        address: formData.address1 ?? '',
        address2: formData.address2 ?? '',
        city: formData.city ?? '',
        region: formData.region ?? '',
        zip: formData.zip ?? '',
        country: formData.country ?? '',
        fund: copyFunds && copyFunds.length > 0 ? copyFunds[0].fundName : '',
        formName: copyFunds && copyFunds.length > 0 ? copyFunds[0].fundraiser : '',
        formID: copyFunds && copyFunds.length > 0 ? copyFunds[0].formID : '',
        tip: 0,
        orgName: formData.organizationName,
        funds: copyFunds && copyFunds.length > 0 ? copyFunds : [],
        schedulePayment: schedulePayment,
        paymentType: getEffectivePaymentType(),
        elementID: '', // add later
        tickets: null, // add in later,
        timezone: getTimezone(props.merchant),
        taxDeductible: formData.taxDeductible ?? isTaxExempt,
        communicationConsent: formData.communicationConsent || false,
        // Dedication fields
        dedicationType: formData.dedicationType || '',
        dedicationName: formData.dedicationName || '',
        dedicationMessage: formData.dedicationMessage || '',
      };

      const requestBody = {
        ...baseRequestBody,
        elementID: formData.formToFormDataMap?.[copyFunds[0].fundraiser]?.elementID ?? '',
        formID: formData.formToFormDataMap?.[copyFunds[0].fundraiser]?.elementID ?? '',
      };

      // Add processor-specific endpoint
      const endpoint = `${
        import.meta.env.VITE_APP_DASHBOARD_TRANSACTIONS_URL
      }/checkout/create-donation`;

      try {
        const authToken = await getAuthToken();
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            accept: '*/*',
            'content-type': 'application/json',
            Authorization: authToken,
          },
          body: JSON.stringify(requestBody),
        });
        const responseBody = await response.json();

        if (!response.ok) {
          setIsSubmitting(false);
          const parsedError = parsePaymentError(
            null,
            response,
            responseBody,
            paymentProcessor || 'STRIPE',
          );
          console.error('Payment error:', parsedError.code, parsedError.userMessage, responseBody);
          toast.error(parsedError.userMessage);
          return;
        }

        // Success handling
        props.grabSelectableFunds?.(await getAuthToken());
        props.setOpen(false);
        setIsSubmitting(false);
        clearStates();

        // Call the callback with the new transaction data if provided
        if (props.onTransactionCreated && responseBody && !responseBody.message) {
          // Check if responseBody is an array or single object
          if (Array.isArray(responseBody)) {
            // Handle array of donation objects - use the new method if available
            if (props.onMultipleTransactionsCreated) {
              props.onMultipleTransactionsCreated(responseBody);
            } else {
              // Fallback to individual calls
              responseBody.forEach((donation) => {
                props.onTransactionCreated?.(donation);
              });
            }
          } else {
            // Handle single donation object
            props.onTransactionCreated(responseBody);
          }
        }

        // Success message based on processor
        const successMessage =
          paymentProcessor === 'FINIX'
            ? 'Finix payment processed successfully!'
            : 'Payment processed successfully!';
        toast.success(successMessage);
      } catch (error) {
        setIsSubmitting(false);
        const errorMessage =
          error instanceof Error ? error.message : 'An unexpected error occurred';
        toast.error(errorMessage);

        console.error('Payment processing error:', {
          processor: paymentProcessor,
          error: error,
        });
      }
    } catch (outerError) {
      console.error('Payment payload construction error:', outerError);
      toast.error('An unexpected error occurred while preparing the payment');
      setIsSubmitting(false);
    }
  };

  const handleConfirmPaymentLatest = useLatestCallback(handleConfirmPayment);

  // Update the clearStates function to include all necessary resets
  const clearStates = () => {
    setFormData({
      ...initialFormData,
      formToFormDataMap: formData.formToFormDataMap || {},
    });

    setDonationAmount('0');
    setFees(0);
    setFeeCovered(true);
    setIsDisabled(false);
    setInvalidFields(new Set());
    setPaymentMethod('Card');
    setCurrentStep('form_information');
    setForm(null);
    setTokenID('');
  };

  useEffect(() => {
    if (props.presentation !== 'workspace' || !props.setWorkspaceActions) return undefined;

    const actions: WorkspaceHeaderAction[] = [];

    if (isEditMode && isOfflinePayment()) {
      actions.push({
        key: 'delete',
        label: 'Delete',
        variant: 'danger',
        disabled: isSubmitting,
        onClick: () => setShowDeleteModal(true),
      });
    }

    actions.push({
      key: 'confirm',
      label: isEditMode
        ? isSubmitting
          ? 'Saving...'
          : 'Save Payment'
        : isSubmitting
          ? 'Processing...'
          : 'Confirm Payment',
      variant: 'primary',
      disabled: isEditMode ? isSubmitting : isDisabled || isSubmitting,
      onClick: handleConfirmPaymentLatest,
    });

    props.setWorkspaceActions(actions);
    return () => props.setWorkspaceActions?.(null);
    // Register current button state without depending on recreated handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.presentation,
    props.setWorkspaceActions,
    isSubmitting,
    isDisabled,
    isEditMode,
    handleConfirmPaymentLatest,
  ]);

  // Add savePayment function for edit mode
  const savePayment = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const headers = editDonationHeaders(await getAuthToken());
      // Calculate total amount from all funds for offline payments
      const offlineTotalAmount = formData.funds.reduce((total, fund) => {
        const amount = parseFloat(String(fund.amount)) || 0;
        const quantity = parseInt(String(fund.quantity)) || 1;
        return total + amount * quantity;
      }, 0);

      // Prepare the updated transaction data for the new Lambda function
      const updatedTransactionData = {
        contactID: formData.contactID,
        transactionID: props.transaction?.transactionID,
        name: `${formData.firstName} ${formData.lastName}`,
        organizationName: formData.organizationName,
        ...(getEffectivePaymentType() !== DEFAULT_PAYMENT_TYPE
          ? {
              amount: offlineTotalAmount,
              fees: formData.coverTransactionFee ? fees : 0,
              // The backend ignores `fees` on edit and prices a covered offline
              // fee from `customFee` (PE-2081), so send the operator's value there.
              ...(formData.coverTransactionFee
                ? { customFee: parseFloat(String(formData.customFeeAmount || 0)) || 0 }
                : {}),
              coverFees: formData.coverTransactionFee,
              timestamp: formData.timestamp,
            }
          : {}),
        email:
          formData.email && formData.email.trim() && formData.email !== '-' ? formData.email : '',
        phone: formData.phone || '',
        address: formData.address1 || '',
        address2: formData.address2 || '',
        city: formData.city || '',
        region: formData.region || '',
        zip: formData.zip || '',
        country: formData.country || '',
        paymentType: getEffectivePaymentType(),
        anonymous: formData.anonymous,
        // No tip: this editor has no tip input, and an absent tip keeps the stored one
        // (sending 0 erased a donor's tip on every edit, PE-2235).
        // Include custom inputs
        customInputs: [
          // Global custom inputs
          ...(formData.globalCustomInputs?.map((input) => ({
            ...input,
            answers: [input.value],
          })) || []),
          // Purchaser custom inputs
          ...(formData.purchaserCustomInputs?.map((input) => ({
            ...input,
            answers: [input.value],
          })) || []),
          // Fund custom inputs
          ...(formData.funds?.flatMap((fund) =>
            fund.customInputs?.map((input) => ({
              ...input,
              inputType: 'fund',
              fundId: fund.formID,
              answers: [input.value],
              cartItemKey: `${fund.frequency}_${fund.fundName}_${parseFloat(
                String(fund.amount),
              ).toFixed(2)}_${fund.fundraiser}`,
            })),
          ) || []),
        ],
        // Include funds if they've changed
        funds: formData.funds.map((fund) => {
          // Clean up fund data similar to the main donation flow
          const cleanFund: Record<string, unknown> = {
            ...fund,
            amount: parseFloat(String(fund.amount)),
            frequency: fund.frequency.replace(/-/g, '_').toUpperCase(),
            nextChargeDate: fund.nextChargeDate
              ? applyChargeTime(fund.nextChargeDate, fund.chargeTime)
              : null,
            endChargeDate:
              fund.endChargeDate && fund.endChargeDate !== 'INFINITY'
                ? applyChargeTime(fund.endChargeDate, fund.chargeTime)
                : fund.endChargeDate,
          };
          if (getEffectivePaymentType() !== DEFAULT_PAYMENT_TYPE) {
            // For offline payments, remove date fields since they're just records
            delete cleanFund.nextChargeDate;
            delete cleanFund.endChargeDate;
            delete cleanFund.chargeTime;
          }

          // Always add productID and productName, even if empty
          cleanFund.productID =
            fund.product && typeof fund.product === 'object'
              ? (fund.product as ProductInfo).productID
              : '';
          cleanFund.productName =
            fund.product && typeof fund.product === 'object'
              ? (fund.product as ProductInfo).productName
              : '';
          delete cleanFund.product;

          // Remove fields that shouldn't be sent to the API
          delete cleanFund.fundraiserHasSponsershipGroups;
          delete cleanFund.customInputs;

          return cleanFund;
        }),
        // Include form name
        formName: formData.funds.length > 0 ? formData.funds[0].fundraiser : '',
        formID: formData.funds.length > 0 ? formData.funds[0].formID : '',
        taxDeductible: formData.taxDeductible ?? isTaxExempt,
        timezone: getTimezone(props.merchant),
        // Dedication fields
        dedicationType: formData.dedicationType || '',
        dedicationName: formData.dedicationName || '',
        dedicationMessage: formData.dedicationMessage || '',
        // Communication consent
        communicationConsent: formData.communicationConsent || false,
      };

      const endpoint = `${
        import.meta.env.VITE_APP_DASHBOARD_TRANSACTIONS_URL
      }/checkout/edit-donation`;

      // Call the new Lambda function endpoint
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatedTransactionData),
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Handle specific error responses
        if (response.status === 404) {
          throw new Error('Donation not found');
        } else if (response.status === 403) {
          throw new Error("You don't have permission to edit this donation");
        } else if (response.status === 400) {
          const errorMessage =
            typeof responseData === 'string' ? responseData : JSON.stringify(responseData);
          throw new Error(errorMessage || 'Invalid request data');
        } else {
          const errorMessage =
            typeof responseData === 'string' ? responseData : JSON.stringify(responseData);
          throw new Error(errorMessage || 'Failed to update payment');
        }
      }

      // Success handling
      props.grabSelectableFunds?.(await getAuthToken());
      props.setOpen(false);
      clearStates();
      setIsSubmitting(false);

      // Update the donations table with the new data

      if (props.updateData) {
        // Pass the updated transaction data to updateData
        props.updateData(null, responseData.updatedDonation);
      }

      toast.success(responseData.message);

      // Log the response for debugging
    } catch (error) {
      console.error('Error updating payment:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update payment. Please try again.';
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  };

  // Delete payment handler (prototype - backend implementation needed)
  const handleDeletePayment = async () => {
    if (!props.transaction?.transactionID) {
      toast.error('Transaction ID not found');
      return;
    }

    // Prototype implementation - replace with actual API call
    console.log(
      'Delete payment prototype - would delete transaction:',
      props.transaction.transactionID,
    );
    toast.info('Delete payment functionality will be implemented by backend developer');
  };

  const fetchFormElementData = async () => {
    const result = await fetchWithAuthRetry(
      import.meta.env.VITE_APP_FORMS_URL,
      {
        method: 'GET',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
        },
      },
      { context: 'fetchFormElementData' },
    );

    if (!result.ok) {
      if (result.status === 403) {
        throw new Error("You don't have permission to view forms. Please try refreshing.");
      } else if (result.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (result.status === 404) {
        throw new Error('Forms not found');
      } else {
        const errorDetail =
          typeof result.error === 'string' ? result.error : 'Failed to load forms';
        throw new Error(errorDetail);
      }
    }

    const responseData = result.data;
    const formToFormDataMap: Record<string, FundraiserData> = {};

    responseData.forEach((form: FundraiserData & { formName: string }) => {
      formToFormDataMap[form.formName] = form;
    });
    const formToTicketMapping: Record<string, unknown[]> = {};

    setFormData((prev) => ({
      ...prev,
      formToFormDataMap: formToFormDataMap,
      formToTicketMapping: formToTicketMapping,
    }));
  };

  useEffect(() => {
    fetchFormElementData().catch((err) => {
      console.error('Failed to load form element data:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load form data');
    });
  }, []);

  // REMOVED: useEffect that directly mutated props.merchant.defaultPaymentProcessor = 'FINIX'
  // This caused a render loop when defaultPaymentProcessor was absent (V2 orgs pre-verification).
  // Replaced by effectivePaymentProcessor derived constant at component top.

  useEffect(() => {
    setTimeout(() => {
      ReactTooltip.rebuild();
    }, 100);
    if (props.open) {
      fetchFormElementData().catch((err) => {
        console.error('Failed to load form element data:', err);
        toast.error(err instanceof Error ? err.message : 'Failed to load form data');
      });
    }
  }, [props.open]);

  const handlePointerDownOutside = (event: {
    target: EventTarget | null;
    preventDefault: () => void;
  }) => {
    // Check if the click is within any element with a class starting with 'mbx'
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('[class^="mbx"]')) {
      event.preventDefault(); // Prevent the dialog from closing
    }
  };

  const workspaceStatus = isEditMode
    ? getRecordStatusInfo(props.transaction?.state)
    : { statusLabel: 'Draft', variant: 'neutral' as const };
  const workspaceContactName = isEditMode
    ? [formData.firstName, formData.lastName].filter(Boolean).join(' ').trim() ||
      formData.organizationName ||
      formData.email ||
      'Payment'
    : 'New Payment';
  const paymentEditorSummary = getPaymentEditorSummary({
    payment: props.transaction,
    offline: isOfflinePayment(),
    fundsTotal: fundsTotal(formData.funds),
    fees,
    coverFees: formData.coverTransactionFee,
  });
  const workspaceAmount = isEditMode
    ? formatSummaryCurrency(paymentEditorSummary.total)
    : formatSummaryCurrency(Number(donationAmount || 0) + (feeCovered ? Number(fees || 0) : 0));
  const closeWorkspace = () => {
    props.setOpen(false);
    clearStates();
  };
  const workspaceHeaderActions = (
    <>
      {isEditMode && isOfflinePayment() && (
        <Button
          type="button"
          variant="danger"
          size="workspace"
          className={HEADER_ACTION_BUTTON_CLASS}
          onClick={() => setShowDeleteModal(true)}
          disabled={isSubmitting}
        >
          Delete
        </Button>
      )}
      <Button
        type="button"
        variant="primary"
        size="workspace"
        className={HEADER_ACTION_BUTTON_CLASS}
        onClick={handleConfirmPaymentLatest}
        disabled={isEditMode ? isSubmitting : isDisabled || isSubmitting}
      >
        {isEditMode
          ? isSubmitting
            ? 'Saving…'
            : 'Save changes'
          : isSubmitting
            ? 'Processing…'
            : 'Confirm payment'}
      </Button>
      <HeaderCloseAction
        label={isEditMode ? 'Close payment editor' : 'Close payment creator'}
        onClick={closeWorkspace}
        disabled={isSubmitting}
      />
    </>
  );

  return (
    <Sheet
      embedded={props.presentation === 'workspace'}
      open={props.open}
      onOpenChange={(open) => {
        if (!open) {
          clearStates();
        }
        // Form reset for create mode is handled by the useEffect that watches props.open
        props.setOpen(open);
      }}
    >
      <SheetContent
        embedded={props.presentation === 'workspace'}
        side="bottom"
        onPointerDownOutside={handlePointerDownOutside}
        className={
          props.presentation === 'workspace'
            ? 'h-full overflow-y-auto rounded-none bg-white p-0'
            : 'pointer-events-none h-[95vh] overflow-hidden rounded-t-[10px] bg-white'
        }
      >
        {/* Main Container - centered */}
        <div
          className={`mx-auto w-full ${
            props.presentation === 'workspace'
              ? 'min-h-full bg-white'
              : 'pointer-events-none h-full max-w-[1600px]'
          }`}
        >
          {props.presentation === 'workspace' && (
            <EntityIdentityHeader
              title={workspaceContactName}
              identifier={props.transaction?.transactionID}
              identifierLabel="Payment ID"
              onCopyIdentifier={(value: string) => navigator.clipboard.writeText(value)}
              metadata={
                isEditMode
                  ? [
                      { key: 'amount', value: workspaceAmount, emphasis: true },
                      { key: 'type', value: 'Fundraising payment' },
                    ]
                  : [{ key: 'type', value: 'Fundraising payment' }]
              }
              statusLabel={isEditMode ? workspaceStatus.statusLabel : undefined}
              statusVariant={isEditMode ? workspaceStatus.variant : undefined}
              actions={workspaceHeaderActions}
            />
          )}
          {/* Header with Close Button */}
          {props.presentation !== 'workspace' && (
            <div className="pointer-events-auto mb-4 flex items-center justify-between rounded-lg bg-white p-0">
              <div className="flex items-start space-x-3">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[19px] border border-edge bg-paper-inset">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="size-6"
                  >
                    <path d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 0 1-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004ZM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 0 1-.921.42Z" />
                    <path
                      fillRule="evenodd"
                      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v.816a3.836 3.836 0 0 0-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 0 1-.921-.421l-.879-.66a.75.75 0 0 0-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 0 0 1.5 0v-.81a4.124 4.124 0 0 0 1.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 0 0-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 0 0 .933-1.175l-.415-.33a3.836 3.836 0 0 0-1.719-.755V6Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-base text-ink-muted">
                    {isEditMode ? 'Edit Payment' : 'Create Payment'}
                  </p>
                  <p className="text-sm text-ink-muted">
                    {isEditMode
                      ? 'Modify payment details and contact information.'
                      : 'Set up and process a payment. Accept donations, sell event tickets, or log offline payments.'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Close Button */}
                <Button
                  variant="ghost"
                  onClick={() => {
                    props.setOpen(false);
                    clearStates();
                  }}
                >
                  Close
                </Button>

                {/* Save Button - Only in edit mode */}
                {isEditMode && (
                  <>
                    {/* Delete Button - Only for offline payments */}
                    {isOfflinePayment() && (
                      <Button
                        onClick={() => setShowDeleteModal(true)}
                        disabled={isSubmitting}
                        variant="danger"
                        size="workspace"
                      >
                        Delete
                      </Button>
                    )}
                    <Button
                      onClick={handleConfirmPayment}
                      disabled={isSubmitting}
                      variant="primary"
                      size="workspace"
                    >
                      {isSubmitting ? 'Saving...' : 'Save Payment'}
                    </Button>
                  </>
                )}

                {/* Confirm Payment Button - Always visible in create mode */}
                {!isEditMode && (
                  <Button
                    onClick={handleConfirmPayment}
                    disabled={isDisabled || isSubmitting}
                    variant="primary"
                    size="workspace"
                  >
                    {isSubmitting ? 'Processing...' : 'Confirm Payment'}
                  </Button>
                )}
              </div>
            </div>
          )}
          {props.presentation !== 'workspace' && (
            <div className="pb-3">
              <Separator className="bg-paper-inset" />
            </div>
          )}
          {/* Two Column Layout - Equal Width with Single Scroll */}
          <div
            className={`pointer-events-none grid grid-cols-1 ${
              props.presentation === 'workspace'
                ? 'items-start gap-4 px-3 py-4 xl:grid-cols-[minmax(0,1fr)_340px]'
                : 'h-[calc(100%-88px)] gap-6 overflow-y-auto lg:grid-cols-2 lg:overflow-y-hidden'
            }`}
          >
            {/* Left Panel - Donation Summary - Fixed */}
            <div
              className={`pointer-events-auto relative flex flex-col gap-2 ${
                props.presentation === 'workspace' ? 'order-2' : ''
              }`}
            >
              <WorkspaceSectionCard
                title={isEditMode ? 'Updated payment' : 'Payment summary'}
                description={
                  isEditMode && !isOfflinePayment()
                    ? 'Recorded charges stay the same when payment details are updated.'
                    : 'A live summary of the payment details.'
                }
                icon={ReceiptPercentIcon}
                bodyClassName="space-y-3"
                enabled={props.presentation === 'workspace'}
              >
                {isEditMode ? (
                  <>
                    <div
                      className={
                        props.presentation === 'workspace'
                          ? 'relative'
                          : 'relative rounded-lg border border-edge bg-paper-muted p-6'
                      }
                    >
                      {/* Summary Content */}
                      <div className="space-y-4">
                        <div className="flex flex-col gap-3">
                          {formData.funds.map((fund, index) => (
                            <div key={index}>
                              <div className="flex justify-between">
                                <div className="flex flex-col space-y-[-2px]">
                                  <span className="text-ink-muted">
                                    {fund.fundraiser ? fund.fundraiser : 'Fundraiser'}{' '}
                                    {fund.quantity > 1 && `x${fund.quantity}`}
                                  </span>
                                  <div className="text-xs text-ink-muted">
                                    {fund.productGroup && <span>{fund.productGroup} - </span>}
                                    {typeof fund.product === 'object' &&
                                      fund.product?.productName && (
                                        <span>{fund.product.productName} - </span>
                                      )}
                                    {fund.fundName && <span>{fund.fundName}</span>}
                                  </div>
                                </div>
                                <div className="flex flex-col items-end">
                                  <span className="text-base text-ink-muted">
                                    {formatSummaryCurrency(
                                      (parseFloat(String(fund.amount)) || 0) * fund.quantity,
                                    )}
                                    {fund.frequency !== 'One-Time' && (
                                      <>
                                        /
                                        {{
                                          daily: 'day',
                                          weekly: 'wk',
                                          monthly: 'mo',
                                          annually: 'yr',
                                        }[fund.frequency.toLowerCase()] || fund.frequency}
                                      </>
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {paymentEditorSummary.support > 0 && (
                          <div className="flex justify-between">
                            <span className="text-base text-ink-muted">Additional support</span>
                            <span className="text-base text-ink-muted">
                              {formatSummaryCurrency(paymentEditorSummary.support)}
                            </span>
                          </div>
                        )}
                        {paymentEditorSummary.discount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-base text-ink-muted">Discount</span>
                            <span className="text-base text-ink-muted">
                              {formatSummaryCurrency(-paymentEditorSummary.discount)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between gap-4">
                          <div>
                            <span className="text-base text-ink-muted">Processing fee</span>
                            <p className="text-xs text-ink-muted">
                              {paymentEditorSummary.coverageNote}
                            </p>
                          </div>
                          <span className="text-base text-ink-muted">
                            {formatSummaryCurrency(paymentEditorSummary.displayedFee)}
                          </span>
                        </div>
                        <div className="border-t border-edge pt-4">
                          <div className="flex justify-between">
                            <span className="text-base text-ink-muted">
                              {isOfflinePayment() ? 'Total' : 'Total charged'}
                            </span>
                            <span className="text-base text-ink-muted">
                              {formatSummaryCurrency(paymentEditorSummary.total)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {formData.funds.filter(
                      (fund) => fund.frequency === 'One-Time' || fund.chargeTime === 'now',
                    ).length > 0 && (
                      <div
                        className={
                          props.presentation === 'workspace'
                            ? 'relative'
                            : 'relative rounded-lg border border-edge bg-paper-muted p-6'
                        }
                      >
                        {/* Summary Content */}
                        <div className="space-y-4">
                          <div className="flex flex-col gap-3">
                            {formData.funds
                              .filter(
                                (fund) =>
                                  fund.frequency === 'One-Time' || fund.chargeTime === 'now',
                              )
                              .map((fund, index) => (
                                <div key={index}>
                                  <div className="flex justify-between">
                                    <div className="flex flex-col space-y-[-2px]">
                                      <span className="text-ink-muted">
                                        {fund.fundraiser ? fund.fundraiser : 'Fundraiser'}{' '}
                                        {fund.quantity > 1 && `x${fund.quantity}`}
                                      </span>
                                      <div className="text-xs text-ink-muted">
                                        {fund.productGroup && <span>{fund.productGroup} - </span>}
                                        {typeof fund.product === 'object' &&
                                          fund.product?.productName && (
                                            <span>{fund.product.productName} - </span>
                                          )}
                                        {fund.fundName && <span>{fund.fundName}</span>}
                                      </div>
                                    </div>
                                    <div className="flex flex-col items-end">
                                      <span className="text-base text-ink-muted">
                                        {formatSummaryCurrency(
                                          (parseFloat(String(fund.amount)) || 0) * fund.quantity,
                                        )}
                                        {fund.frequency !== 'One-Time' && (
                                          <>
                                            /
                                            {{
                                              daily: 'day',
                                              weekly: 'wk',
                                              monthly: 'mo',
                                              annually: 'yr',
                                            }[fund.frequency.toLowerCase()] || fund.frequency}
                                          </>
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>

                          <div className="flex justify-between">
                            <span className="text-base text-ink-muted">Fee</span>
                            {getEffectivePaymentType() === DEFAULT_PAYMENT_TYPE &&
                            formData.coverTransactionFee ? (
                              <span className="text-base text-ink-muted">
                                {formatSummaryCurrency(fees || 0)}
                              </span>
                            ) : getEffectivePaymentType() !== DEFAULT_PAYMENT_TYPE &&
                              formData.feeCovered ? (
                              <span className="text-base text-ink-muted">
                                {formatSummaryCurrency(
                                  parseFloat(String(formData.customFeeAmount || 0)),
                                )}
                              </span>
                            ) : (
                              <span className="text-base text-ink-muted">
                                {formatSummaryCurrency(0)}
                              </span>
                            )}
                          </div>

                          <div className="border-t border-edge pt-4">
                            <div className="flex justify-between">
                              <span className="text-base text-ink-muted">Today&apos;s Total</span>
                              <span className="text-base text-ink-muted">
                                {(() => {
                                  if (
                                    getEffectivePaymentType() === DEFAULT_PAYMENT_TYPE &&
                                    formData.coverTransactionFee
                                  ) {
                                    return formatSummaryCurrency(
                                      fundsTotal(formData.funds) + (fees || 0),
                                    );
                                  } else if (
                                    getEffectivePaymentType() !== DEFAULT_PAYMENT_TYPE &&
                                    formData.feeCovered
                                  ) {
                                    return formatSummaryCurrency(
                                      fundsTotal(formData.funds) +
                                        parseFloat(String(formData.customFeeAmount || 0)),
                                    );
                                  } else {
                                    return formatSummaryCurrency(fundsTotal(formData.funds));
                                  }
                                })()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {formData.funds.filter(
                      (fund) => fund.frequency !== 'One-Time' && fund.chargeTime !== 'now',
                    ).length > 0 && (
                      <div
                        className={
                          props.presentation === 'workspace'
                            ? 'relative border-t border-edge pt-4'
                            : 'relative rounded-lg border border-edge bg-paper-muted p-6'
                        }
                      >
                        {/* Summary Content */}
                        <div className="space-y-4">
                          <div className="flex flex-col gap-3">
                            {formData.funds
                              .filter(
                                (fund) =>
                                  fund.frequency !== 'One-Time' && fund.chargeTime !== 'now',
                              )
                              .map((fund, index) => (
                                <div key={index}>
                                  <div className="flex justify-between">
                                    <div className="flex flex-col space-y-[-2px]">
                                      <span className="text-ink-muted">
                                        {fund.fundraiser ? fund.fundraiser : 'Fundraiser'}{' '}
                                        {fund.quantity > 1 && `x${fund.quantity}`}
                                      </span>
                                      <div className="text-xs text-ink-muted">
                                        {fund.productGroup && <span>{fund.productGroup} - </span>}
                                        {typeof fund.product === 'object' &&
                                          fund.product?.productName && (
                                            <span>{fund.product.productName} - </span>
                                          )}
                                        {fund.fundName && <span>{fund.fundName}</span>}
                                      </div>
                                    </div>
                                    <div className="flex flex-col items-end">
                                      <span className="text-base text-ink-muted">
                                        {formatSummaryCurrency(
                                          (parseFloat(String(fund.amount)) || 0) * fund.quantity,
                                        )}
                                        {fund.frequency !== 'One-Time' && (
                                          <>
                                            /
                                            {{
                                              daily: 'day',
                                              weekly: 'wk',
                                              monthly: 'mo',
                                              annually: 'yr',
                                            }[fund.frequency.toLowerCase()] || fund.frequency}
                                          </>
                                        )}
                                      </span>
                                      {/* Show next charge date for subscriptions — falls back to today when nextChargeDate is null */}
                                      {fund.frequency !== 'One-Time' &&
                                        fund.chargeTime !== 'now' && (
                                          <div className="ml-4 flex flex-col items-end justify-between text-right text-xs text-ink-muted sm:flex-row sm:items-start sm:gap-1">
                                            <span>Next Charge:</span>
                                            <span>
                                              {new Date(
                                                fund.nextChargeDate ?? Date.now(),
                                              ).toLocaleDateString()}
                                            </span>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>

                          {/* Subscription Fee Section */}
                          {(() => {
                            const subscriptionFees = calculateSubscriptionFees(
                              formData.funds,
                              props.merchant,
                              formData.paymentMethod,
                            );

                            return (
                              <div className="flex justify-between">
                                <span className="text-base text-ink-muted">Fee</span>
                                <span className="text-base text-ink-muted">
                                  {formatSummaryCurrency(
                                    subscriptionFees > 0 ? subscriptionFees : 0,
                                  )}
                                </span>
                              </div>
                            );
                          })()}

                          {/* Subscription Total Section */}
                          {(() => {
                            const subscriptionFunds = formData.funds.filter(
                              (fund) => fund.frequency !== 'One-Time' && fund.chargeTime !== 'now',
                            );

                            const subscriptionTotal = subscriptionFunds.reduce((total, fund) => {
                              const amount = parseFloat(String(fund.amount)) || 0;
                              const quantity = parseInt(String(fund.quantity)) || 1;
                              return total + amount * quantity;
                            }, 0);

                            // Calculate subscription fees if they should be covered
                            const subscriptionFees =
                              !isEditMode && feeCovered
                                ? calculateSubscriptionFees(
                                    formData.funds,
                                    props.merchant,
                                    formData.paymentMethod,
                                  )
                                : 0;

                            const totalWithFees = subscriptionTotal + subscriptionFees;

                            return (
                              <div className="border-t border-edge pt-4">
                                <div className="flex justify-between">
                                  <span className="text-base text-ink-muted">Future Total</span>
                                  <span className="text-base text-ink-muted">
                                    {formatSummaryCurrency(totalWithFees)}
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </WorkspaceSectionCard>
              {props.presentation === 'workspace' && (
                <TaxDeductibleCard
                  checked={formData.taxDeductible !== false}
                  onCheckedChange={(taxDeductible) =>
                    setFormData((previous) => ({ ...previous, taxDeductible }))
                  }
                />
              )}
            </div>

            {/* Right Panel - Form Fields - Only Scrollable Panel */}
            <div
              className={`pointer-events-auto min-w-0 rounded-lg bg-white ${
                props.presentation === 'workspace'
                  ? 'compact-editor-controls order-1'
                  : 'pr-0 [-ms-overflow-style:none] [scrollbar-width:none] lg:overflow-y-auto [&::-webkit-scrollbar]:hidden'
              }`}
            >
              <FundraiserLayout
                presentation={props.presentation}
                stripeCardRef={stripeCardRef}
                finixCardRef={finixCardRef}
                finixBankRef={finixBankRef}
                donationAmount={donationAmount}
                formData={formData}
                setFormData={setFormData}
                invalidFields={visibleInvalidFields}
                setInvalidFields={setInvalidFields}
                isFieldInvalid={isFieldInvalid}
                handleFieldFocus={handleFieldFocus}
                handleCustomAmountChange={handleCustomAmountChange}
                handleFeeCoverageChange={handleFeeCoverageChange}
                merchant={props.merchant}
                fees={fees}
                feeCovered={feeCovered}
                setFeeCovered={setFeeCovered}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                form={form}
                setForm={setForm}
                setTokenID={setTokenID}
                isDrawerOpen={props.open}
                handleBack={handleBack}
                handleContinue={handleContinue}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                isDisabled={isDisabled}
                isSubmitting={isSubmitting}
                isEditMode={isEditMode}
                transaction={props.transaction}
                calculateFees={calculateFees}
                linkedSubscription={props.linkedSubscription}
                isToday={isToday}
                setFees={setFees}
                onDeletePayment={handleDeletePayment}
                selectedContact={selectedContact}
                setSelectedContact={setSelectedContact}
                isNewContact={isNewContact}
                setIsNewContact={setIsNewContact}
              />
            </div>
          </div>
        </div>
      </SheetContent>

      {/* Delete Payment Modal */}
      {isOfflinePayment() && props.transaction && (
        <DeletePaymentModal
          open={showDeleteModal}
          setOpen={setShowDeleteModal}
          onConfirm={() => {
            handleDeletePayment();
            setShowDeleteModal(false);
          }}
          paymentDetails={{
            amount: parseFloat(String(props.transaction.grossAmount)) || 0,
            date: props.transaction.timestamp || '',
            paymentType: getEffectivePaymentType(),
            name: props.transaction.name || `${formData.firstName} ${formData.lastName}`,
          }}
        />
      )}
    </Sheet>
  );
}
